import { formatAmount } from "../../include/money";
import { useCallback, useContext, useEffect, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { QRCodeCanvas } from "qrcode.react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { callPostApi } from "../../function/ApiCallFunction";
import { adminPost } from "../admin/adminApi";
import { clubPost } from "../club/clubApi";
import { DataContext } from "../../context/DataContext";
import { consoleColors, primaryButtonSx } from "../admin/consoleTheme";
import { indiaDate, indiaDateTime } from "../../include/indiaDate";

export default function LuckyDrawResult() {
  const { drawId } = useParams();
  const [params, setParams] = useSearchParams();
  const view = params.get("view") || "result";
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/console");
  const isClub = location.pathname.startsWith("/club");
  const { loginData, clubData } = useContext(DataContext);
  const storeId = loginData?.store_info?.store_id;
  const clubId = clubData?.club_info?.club_id;

  const drawCall = useCallback((mod, extra = {}) => {
    if (isAdmin) return adminPost(mod, { draw_id: drawId, ...extra });
    if (isClub) return clubPost(mod, { club_id: clubId, draw_id: drawId, ...extra });
    return callPostApi("store-draw", { mod, data_arr: { store_id: storeId, draw_id: drawId, ...extra } });
  }, [isAdmin, isClub, drawId, storeId, clubId]);
  const [draw, setDraw] = useState(null);
  const [rows, setRows] = useState([]);
  const [slots, setSlots] = useState([]);
  const [slotKey, setSlotKey] = useState("");
  const [mode, setMode] = useState("frequency");
  const [issuing, setIssuing] = useState(false);

  const loadDraw = useCallback(async () => {
    const result = await drawCall("DRAW_DETAIL");
    if (result.status === "200") setDraw(result.data.draw);
  }, [drawCall]);

  const loadRows = useCallback(async () => {
    const mod = view === "winners" ? "DRAW_WINNERS" : "DRAW_ISSUED";
    const result = await drawCall(mod);
    if (result.status === "200") setRows(result.data?.entries || result.data?.winners || []);
  }, [drawCall, view]);

  const loadSlots = useCallback(async (nextMode) => {
    const result = await drawCall("DRAW_SLOTS", { result_mode: nextMode === "all" ? "all" : "frequency" });
    if (result.status === "200") {
      const next = result.data?.slots || [];
      setSlots(next);
      setSlotKey((current) => next.some((item) => item.key === current) ? current : (next[0]?.key || ""));
    }
  }, [drawCall]);

  const ready = isAdmin || (isClub ? Boolean(clubId) : Boolean(storeId));
  const listPath = isAdmin ? "/console/luckydraw" : isClub ? "/club/luckydraw" : "/luckydraws";
  const winnerPath = isAdmin ? "/console/luckydraw-winner" : isClub ? "/club/luckydraw-winner" : "/luckydrawwinner";
  useEffect(() => { if (ready) loadDraw(); }, [ready, loadDraw]);
  useEffect(() => { if (ready && view !== "result") loadRows(); }, [ready, view, loadRows]);
  useEffect(() => { if (ready && (view === "result" || view === "issued")) loadSlots(mode); }, [ready, view, mode, loadSlots]);

  const issue = async () => {
    setIssuing(true);
    try {
      const result = await drawCall("DRAW_ISSUE_WINNER", { result_mode: mode === "all" ? "all" : "frequency", slot_key: slotKey });
      if (result.status === "200" && result.data?.winner) {
        navigate(winnerPath, { state: { ...result.data, draw_id: drawId } });
      }
      else toast.info(result.status_message || result.data?.error || "Could not issue a winner");
    } finally {
      setIssuing(false);
    }
  };

  useEffect(() => {
    if (view !== "result" || !ready) return;
    drawCall("DRAW_ISSUED").then((result) => {
      const entries = result.data?.entries || [];
      if (!slotKey || mode === "all") {
        setRows(mode === "all" ? entries : entries);
        return;
      }
      setRows(entries);
    });
  }, [view, ready, drawCall, slotKey, mode]);

  const filtered = view === "winners" || mode === "all" || !slotKey
    ? rows
    : rows.filter((row) => matchesSlot(row, slotKey, draw?.frequency));

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Button sx={{ textTransform: "none", mb: 1.5 }} onClick={() => navigate(listPath)}>Back to Lucky Draw Coupons</Button>
      {draw?.banner && <Box component="img" src={draw.banner} alt="Banner" sx={{ width: "100%", maxHeight: 180, objectFit: "cover", borderRadius: 2, mb: 2 }} />}
      <Box sx={{ background: draw?.background_image ? `center/cover url(${draw.background_image})` : "#fff", border: `1px solid ${consoleColors.line}`, borderRadius: 2.5, p: 2, mb: 2 }}>
        <Box sx={{ background: draw?.background_image ? "rgba(255,255,255,0.92)" : "transparent", borderRadius: 2, p: draw?.background_image ? 2 : 0, display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
          {draw && <QRCodeCanvas value={draw.draw_id} size={84} />}
          <Box>
            <Typography sx={{ fontSize: 22, fontWeight: 750, color: consoleColors.ink }}>{draw?.title || "Lucky Draw Coupons"}</Typography>
            <Typography sx={{ fontSize: 13, color: consoleColors.muted }}>{draw?.frequency_label} · {indiaDate(draw?.start_at)} to {indiaDate(draw?.end_at)}</Typography>
            <Typography sx={{ fontSize: 13, color: consoleColors.ink }}>Minimum sale ₹{formatAmount(draw?.min_sale_amount)} · QR {draw?.draw_id}</Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
        {["result", "issued", "winners"].map((item) => (
          <Button key={item} variant={view === item ? "contained" : "outlined"} sx={{ textTransform: "none" }} onClick={() => setParams({ view: item })}>
            {item === "result" ? "Result" : item === "issued" ? "Issued customers" : "Winners"}
          </Button>
        ))}
      </Box>

      {(view === "result" || view === "issued") && (
        <Box sx={{ background: "#fff", border: `1px solid ${consoleColors.line}`, borderRadius: 2, p: 1.5, mb: 2 }}>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1 }}>
            <Button size="small" variant={mode === "frequency" ? "contained" : "outlined"} sx={{ textTransform: "none" }} onClick={() => setMode("frequency")}>By frequency</Button>
            <Button size="small" variant={mode === "all" ? "contained" : "outlined"} sx={{ textTransform: "none" }} onClick={() => setMode("all")}>All</Button>
            {view === "result" && (isAdmin || isClub) && <Button size="small" variant="contained" sx={primaryButtonSx} disabled={issuing || filtered.length === 0} onClick={issue}>{issuing ? "Issuing..." : "Issue winner"}</Button>}
          </Box>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {slots.map((slot) => (
              <Button key={slot.key} size="small" variant={slotKey === slot.key ? "contained" : "outlined"} sx={{ textTransform: "none" }} onClick={() => setSlotKey(slot.key)}>
                {slot.label} · {slot.count}
              </Button>
            ))}
          </Box>
        </Box>
      )}

      <EntryTable rows={view === "winners" ? rows : filtered} winners={view === "winners"} />
    </Box>
  );
}

function matchesSlot(row, slotKey, frequency) {
  if (!slotKey || slotKey === "all") return true;
  const date = String(row.date_added || "").slice(0, 10);
  const hour = String(row.date_added || "").slice(11, 13);
  if (frequency === "day") return date === slotKey;
  if (frequency === "all") return true;
  return `${date}|${hour}` === slotKey;
}

function nameCase(value) {
  return String(value || "").trim().toLowerCase().replace(/(^|\s|-)([a-z])/g, (_, sep, letter) => `${sep}${letter.toUpperCase()}`);
}

function EntryTable({ rows, winners }) {
  const columns = winners
    ? ["Name", "Mobile", "Launch time", "Code"]
    : ["Name", "Mobile", "Email", "Amount", "Code", "Added"];
  const template = winners ? "1.2fr 1fr 1.1fr 0.8fr" : "1.2fr 1fr 1.2fr 0.7fr 0.8fr 1fr";
  return (
    <Box sx={{ background: "#fff", border: `1px solid ${consoleColors.line}`, borderRadius: 2, overflow: "auto" }}>
      <Box sx={{ display: "grid", gridTemplateColumns: template, gap: 1, px: 2, py: 1.2, background: "#f8fafc", minWidth: winners ? 560 : 720 }}>
        {columns.map((label) => (
          <Typography key={label} sx={{ fontSize: 12, fontWeight: 700, color: "#334155" }}>{label}</Typography>
        ))}
      </Box>
      {rows.length === 0 && <Typography sx={{ p: 2, color: consoleColors.muted }}>No records.</Typography>}
      {rows.map((row) => (
        <Box key={row.row_temp_id || row.coupon_code} sx={{ display: "grid", gridTemplateColumns: template, gap: 1, px: 2, py: 1.1, borderTop: `1px solid ${consoleColors.line}`, minWidth: winners ? 560 : 720 }}>
          <Typography sx={{ fontSize: 13.5 }}>{nameCase(row.customer_name)}{row.is_winner === "1" ? " · Winner" : ""}</Typography>
          <Typography sx={{ fontSize: 13.5 }}>{row.customer_mobile}</Typography>
          {winners ? (
            <>
              <Typography sx={{ fontSize: 13.5 }}>{indiaDateTime(row.winner_at || row.date_added)}</Typography>
              <Typography sx={{ fontSize: 13.5 }}>{row.coupon_code}</Typography>
            </>
          ) : (
            <>
              <Typography sx={{ fontSize: 13.5 }}>{row.customer_email || "—"}</Typography>
              <Typography sx={{ fontSize: 13.5 }}>₹{formatAmount(row.order_amount)}</Typography>
              <Typography sx={{ fontSize: 13.5 }}>{row.coupon_code}</Typography>
              <Typography sx={{ fontSize: 13.5 }}>{indiaDate(row.date_added)}</Typography>
            </>
          )}
        </Box>
      ))}
    </Box>
  );
}
